import React, {
  useContext,
  useState,
  useEffect,
  Fragment,
  useRef,
} from "react";
import {
  calculateOrderTotal,
  calculateCompleteOrderTotal,
  getAdditionalOptionsCounts,
} from "../services/beecareOrderService";
import BeeCareOrderContext from "../contexts/BeeCareOrderContext";
import history from "../services/history";
import {
  Typography,
  Box,
  Divider,
  Button,
  Grid,
  OutlinedInput,
  FormControl,
  FormHelperText,
  IconButton,
  Popover,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";
import API from "../services/api";
import SettingsContext from "../contexts/SettingsContext";
import { useMount } from "react-use";
import {
  ArrowForwardIosOutlined,
  Delete,
  HelpOutline,
  Edit,
} from "@mui/icons-material";
import BeeCareOrderSideBar from "./BeeCareOrderSideBar";
import WBPPage from "./sub-components/WBPPage";
import UserContext from "../contexts/UserContext";
import UIContext from "../contexts/UIContext";
import DonationSummary from "./sub-components/DonationSummary";
import references from "../services/references";
import DiscountToolTip from "./sub-components/DiscountToolTip";
import {
  DonationSelector,
  SummaryAddress,
} from "./sub-components/SummaryComponents";
import AdditionalProducts from "./sub-components/AdditionalProducts";
import ProductLine from "./sub-components/ProductLine";

const useStyles = makeStyles((theme) => ({
  formRoot: { width: "100%" },
  btnSize: {
    [theme.breakpoints.up("sm")]: {
      marginBottom: "10px",
      width: "100%",
    },
  },
  padless: {
    padding: 7,
  },
  countColumn: {
    textAlign: "center",
  },
  itemColumn: {},
  priceColumn: {
    textAlign: "end",
  },
  deleteColumn: {
    textAlign: "end",
  },
  mobiWidth: {
    [theme.breakpoints.down("md")]: {
      marginBottom: theme.spacing(1),
      width: "100%",
    },
  },
  totalPrice: {
    color: theme.palette.primary.main,
    fontSize: theme.typography.h4.fontSize,
    fontWeight: "bold",
  },
  divider: {
    width: "100%",
  },
  fullWidth: {
    width: "100%",
  },
  deleteIconButton: {
    padding: 0,
  },
}));

const SendInBoxStep3 = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const classes = useStyles();
  const [beeCareOrder, setBeeCareOrder] = useContext(BeeCareOrderContext);
  const [voucherCode, setVoucherCode] = useState("");
  const settings = useContext(SettingsContext).beecare;
  const [user] = useContext(UserContext);
  const [, , loader] = useContext(UIContext);
  const [voucherError, setVoucherError] = useState("");
  const [currency, setCurrency] = useState("");
  const [price, setPrice] = useState(references.defaultOrders.beecare.price);
  const [anchorEl, setAnchorEl] = useState(null);
  const openPopover = Boolean(anchorEl);
  const popoverId = open ? "simple-popover" : undefined;

  const [additionalProducts, setAdditionalProducts] = useState(
    beeCareOrder.additionalProducts || []
  );
  const isMounted = useRef(true);

  useEffect(() => {
    if (beeCareOrder.amount === 0) {
      history.push("/sendinbox/return-houses");
    }

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    setCurrency(settings ? settings.currency : "");
  }, [settings.currency]);

  useEffect(() => {
    if (beeCareOrder.variant && beeCareOrder.variant.price) {
      setPrice(
        typeof beeCareOrder.variant.price === "object" &&
          beeCareOrder.variant.price.amount
          ? Number(beeCareOrder.variant.price.amount)
          : Number(beeCareOrder.variant.price)
      );
    }
  }, [beeCareOrder.variant]);

  useEffect(() => {
    const newTotal = calculateTotals(
      beeCareOrder.donation || 0,
      beeCareOrder.discount_vouchers || [],
      beeCareOrder.additionalProducts || []
    );
    setBeeCareOrder((prev) => ({
      ...prev,
      total_price: newTotal,
    }));
  }, [
    beeCareOrder.additionalOptions,
    beeCareOrder.donation,
    beeCareOrder.discount_vouchers,
    beeCareOrder.additionalProducts,
    price,
  ]);

  const sendData = (shouldSkip) => {
    // Calculate fresh total including all additions
    const currentTotal = calculateTotals(
      beeCareOrder.donation,
      beeCareOrder.discount_vouchers
    );

    const data = {
      address_id: beeCareOrder.address_id,
      amount: beeCareOrder.amount,
      houses: beeCareOrder.houses,
      vouchers: beeCareOrder.discount_vouchers.map((voucher) => voucher.id),
      total_price: currentTotal,
      additional_options: beeCareOrder.additionalOptions,
      // donate: beeCareOrder.donation,
      type: beeCareOrder.type,
    };

    if (shouldSkip && Number(currentTotal) > 0) {
      data.psp = beeCareOrder.psp;
    }

    loader.addTask("ORDER");
    loader.addTask("CHECKOUT");

    // Use Cart API instead of deprecated Checkout API
    createShopifyCart();
  };
  const createShopifyCart = async () => {
    try {
      // Create cart via GraphQL API
      const shopifyUrl = JSON.parse(process.env.REACT_APP_SHOPIFY_URL)[
        i18n.language
      ];
      const accessToken = JSON.parse(process.env.REACT_APP_SHOPIFY_ACCESSTOKEN)[
        i18n.language
      ];

      // 1. Create cart
      const cartCreateResponse = await fetch(
        `${shopifyUrl}/api/2023-10/graphql.json`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": accessToken,
          },
          body: JSON.stringify({
            query: `
              mutation cartCreate {
                cartCreate {
                  cart {
                    id
                    checkoutUrl
                  }
                  userErrors {
                    message
                    field
                  }
                }
              }
            `,
          }),
        }
      );

      const cartData = await cartCreateResponse.json();
      if (
        cartData.errors ||
        (cartData.data.cartCreate.userErrors &&
          cartData.data.cartCreate.userErrors.length > 0)
      ) {
        throw cartData.errors || cartData.data.cartCreate.userErrors;
      }

      const cartId = cartData.data.cartCreate.cart.id;

      // Get selected address for return address attribute
      const selectedAddress = user.addresses.find(
        (address) => address.id === beeCareOrder.address_id
      );

      // 2. Update cart attributes including tags for additional options
      const cartAttributes = [
        {
          key: "seasonType",
          value: "beecare",
        },
      ];

      // Add return address as cart attribute
      if (selectedAddress) {
        cartAttributes.push({
          key: "Return Address",
          value: JSON.stringify(selectedAddress),
        });
      }

      const attributesResponse = await fetch(
        `${shopifyUrl}/api/2023-10/graphql.json`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": accessToken,
          },
          body: JSON.stringify({
            query: `
              mutation cartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
                cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
                  cart {
                    id
                  }
                  userErrors {
                    message
                    field
                  }
                }
              }
            `,
            variables: {
              cartId,
              attributes: cartAttributes,
            },
          }),
        }
      );

      const attributesData = await attributesResponse.json();
      if (
        attributesData.errors ||
        (attributesData.data.cartAttributesUpdate.userErrors &&
          attributesData.data.cartAttributesUpdate.userErrors.length > 0)
      ) {
        throw (
          attributesData.errors ||
          attributesData.data.cartAttributesUpdate.userErrors
        );
      }

      // Build the shipping address (selectedAddress is already defined above)
      const shippingAddress = {
        address1: selectedAddress.address_1,
        address2: selectedAddress.address_2,
        city: selectedAddress.town,
        company: selectedAddress.company_name,
        country: selectedAddress.country_code,
        firstName: selectedAddress.first_name,
        lastName: selectedAddress.last_name,
        phone: "",
        province: "",
        zip: selectedAddress.postal_code,
      };

      // Build line items
      const lineItems = [];
      const shopifyHouses = beeCareOrder.selectedHouses.join(", ");

      // Add main product if quantity > 0
      const quantity =
        beeCareOrder.amount - beeCareOrder.discount_vouchers.length;
      if (quantity > 0) {
        lineItems.push({
          merchandiseId: beeCareOrder.variant.id,
          quantity,
          attributes: [{ key: "houses", value: shopifyHouses }],
        });
      }

      // Add voucher items if any
      if (beeCareOrder.discount_vouchers.length > 0) {
        const freeVariant = settings.shopify.products.variants.find(
          (variant) => {
            let price = variant.price;
            if (typeof price === "object" && price.amount) price = price.amount;
            return Number(price) === 0;
          }
        );

        lineItems.push({
          merchandiseId: freeVariant.id,
          quantity: beeCareOrder.discount_vouchers.length,
          attributes: [
            {
              key: "vouchers",
              value: beeCareOrder.discount_vouchers
                .map((v) => v.code)
                .join(", "),
            },
            { key: "houses", value: shopifyHouses },
          ],
        });
      }

      // Add donation if present
      if (beeCareOrder.donation > 0) {
        const donationVariant = settings.shopify.donationOptions.variants.find(
          (variant) => variant.sku === `donation-${beeCareOrder.donation}`
        );
        lineItems.push({ merchandiseId: donationVariant.id, quantity: 1 });
      }

      // Add additional products if any
      if (beeCareOrder.additionalProducts?.length > 0) {
        beeCareOrder.additionalProducts.forEach((product) => {
          lineItems.push({
            merchandiseId: product.id,
            quantity: product.quantity,
            attributes: [{ key: "product_type", value: "additional_product" }],
          });
        });
      }

      // Add additionalOptions as line items
      if (
        beeCareOrder.additionalOptions &&
        Array.isArray(beeCareOrder.additionalOptions)
      ) {
        beeCareOrder.additionalOptions.forEach((option) => {
          if (option.addOnDetails && Array.isArray(option.addOnDetails)) {
            option.addOnDetails.forEach((addOn) => {
              lineItems.push({
                merchandiseId: addOn.id,
                quantity: 1,
                attributes: [
                  { key: "house_code", value: option.houseCode || "" },
                  { key: "house_id", value: String(option.houseId || "") },
                  { key: "product_type", value: "additional_option" },
                ],
              });
            });
          }
        });
      }

      // Filter and add line items
      const validLineItems = lineItems.filter((item) => item.quantity > 0);
      if (validLineItems.length > 0) {
        const linesAddResponse = await fetch(
          `${shopifyUrl}/api/2023-10/graphql.json`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token": accessToken,
            },
            body: JSON.stringify({
              query: `
                mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
                  cartLinesAdd(cartId: $cartId, lines: $lines) {
                    cart {
                      id
                      checkoutUrl
                    }
                    userErrors {
                      message
                      field
                    }
                  }
                }
              `,
              variables: {
                cartId,
                lines: validLineItems,
              },
            }),
          }
        );

        const linesData = await linesAddResponse.json();
        if (
          linesData.errors ||
          (linesData.data.cartLinesAdd.userErrors &&
            linesData.data.cartLinesAdd.userErrors.length > 0)
        ) {
          throw linesData.errors || linesData.data.cartLinesAdd.userErrors;
        }

        // Get checkout URL
        let checkoutURL = linesData.data.cartLinesAdd.cart.checkoutUrl;
        if (i18n.language === "fr-CH") {
          checkoutURL += "&locale=fr";
        }

        // Add shipping address to checkout URL if we have one
        if (selectedAddress) {
          const addressParams = new URLSearchParams({
            "checkout[shipping_address][first_name]":
              selectedAddress.first_name || "",
            "checkout[shipping_address][last_name]":
              selectedAddress.last_name || "",
            "checkout[shipping_address][address1]":
              selectedAddress.address1 || selectedAddress.address_1 || "",
            "checkout[shipping_address][address2]":
              selectedAddress.address2 || selectedAddress.address_2 || "",
            "checkout[shipping_address][city]":
              selectedAddress.city || selectedAddress.town || "",
            "checkout[shipping_address][province]":
              selectedAddress.state || selectedAddress.province || "",
            "checkout[shipping_address][zip]":
              selectedAddress.postal_code || "",
            "checkout[shipping_address][country]":
              selectedAddress.country_code || "",
            "checkout[shipping_address][phone]": selectedAddress.phone || "",
          });

          // Append address parameters to checkout URL
          checkoutURL +=
            (checkoutURL.includes("?") ? "&" : "?") + addressParams.toString();
        }

        // Redirect to checkout
        gotoShopifyCheckout(checkoutURL);
      }
    } catch (error) {
      shopifyError(error);
    }
  };
  const gotoShopifyCheckout = (checkoutURL) => {
    API.get(`me/shopify-login?return_to=` + checkoutURL)
      .then((response) => {
        loader.clearTask("CHECKOUT");
        window.open(response.data.url, "_self");
      })
      .catch(shopifyError);
  };

  const shopifyError = (error) => {
    loader.clearTask("CHECKOUT");
    console.warn(error);
  };

  const calculateTotals = (
    donation,
    discount_vouchers,
    additionalProducts = beeCareOrder.additionalProducts
  ) => {
    // Use the new centralized calculation function that handles bulk pricing
    const totals = calculateCompleteOrderTotal({
      selectedHouses: beeCareOrder.selectedHouses,
      allShopifyVariants: settings?.shopify?.products?.variants || [],
      discount_vouchers,
      donation,
      additionalOptions: beeCareOrder.additionalOptions,
      additionalProducts,
    });

    return totals.finalTotal;
  };

  const deleteAllDiscounts = (event) => {
    event.preventDefault();
    setBeeCareOrder((oldOrder) => ({
      ...oldOrder,
      discount_vouchers: [],
    }));
    calculateTotals(beeCareOrder.donation, []);
  };

  // toggleDonations is what we use to differentiate toggleable donation buttons from the custom donation
  const submitDonation = (event, donationValue = 0, toggleDonation = false) => {
    event.preventDefault();
    const newDonation =
      toggleDonation && beeCareOrder.donation === donationValue
        ? 0
        : donationValue;
    setBeeCareOrder((oldOrder) => ({
      ...oldOrder,
      donation: newDonation,
    }));
    calculateTotals(newDonation, beeCareOrder.discount_vouchers);
  };

  const addVoucherHandler = (event) => {
    event.preventDefault();
    setVoucherError("");
    setVoucherCode(event.target.value.toUpperCase().trim());
  };
  const submitVoucherCode = (event) => {
    event.preventDefault();
    setVoucherError("");

    // Check if we can add more vouchers
    if (
      beeCareOrder.discount_vouchers.length >=
      beeCareOrder.selectedHouses.length
    ) {
      setVoucherError(
        t("Cannot add more vouchers than beehomes registered for beecare")
      );
      return;
    }

    if (voucherCode.toUpperCase().indexOf("FREECARE") < 0) {
      if (
        !beeCareOrder.discount_vouchers.some(
          (voucher) => voucher.code === voucherCode
        )
      ) {
        loader.addTask("VOUCHER");
        API.post(`me/vouchers`, { code: voucherCode, type: "BEECARE" })
          .then((res) => {
            loader.clearTask("VOUCHER");
            if (!isMounted.current) return;

            const discount_vouchers = [...beeCareOrder.discount_vouchers];
            discount_vouchers.push(res.data);
            setVoucherCode("");
            setBeeCareOrder((oldOrder) => ({
              ...oldOrder,
              discount_vouchers: discount_vouchers,
            }));
            calculateTotals(beeCareOrder.donation, discount_vouchers);
          })
          .catch((error) => {
            loader.clearTask("VOUCHER");
            console.warn(error);
            setVoucherError(
              t(error.response?.data?.detail || "An error occurred")
            );
          });
      } else {
        setVoucherError(t("Voucher code has already been entered"));
      }
    } else {
      setVoucherError(t("Invalid code"));
    }
  };
  const handleDelete = (product) => {
    if (!isMounted.current) return;

    console.log("Deleting:", product);

    // Filter out the deleted product
    const updatedProducts = additionalProducts.filter(
      (item) => item.id !== product
    );

    // Update state (this triggers a re-render)
    setAdditionalProducts(updatedProducts);
    const newTotal = calculateTotals(
      beeCareOrder.donation ?? 0,
      beeCareOrder.discount_vouchers ?? 0,
      updatedProducts // Pass updated products directly
    );
    setBeeCareOrder((oldOrder) => {
      return {
        ...oldOrder,
        additionalProducts: updatedProducts,
        total_price: newTotal,
      };
    });
  };

  const addProductToOrder = (variant) => {
    if (!isMounted.current) return;

    console.log(variant);

    setBeeCareOrder((oldOrder) => {
      const updatedProducts = oldOrder.additionalProducts
        ? [...oldOrder.additionalProducts]
        : []; // Ensure it's an array

      const existingProduct = updatedProducts.find((p) => p.id === variant.id);

      if (existingProduct) {
        existingProduct.quantity += 1;
      } else {
        updatedProducts.push({
          ...variant,
          quantity: 1,
          price: variant.priceV2.amount,
        });
      }

      const newTotal = calculateTotals(
        oldOrder.donation ?? 0,
        oldOrder.discount_vouchers ?? 0,
        updatedProducts
      );

      setAdditionalProducts(updatedProducts);
      return {
        ...oldOrder,
        additionalProducts: updatedProducts,
        total_price: newTotal,
      };
    });
  };

  const submitHandler = (event) => {
    event.preventDefault();
    sendData(Number(beeCareOrder.total_price) === 0 ? true : false);
  };

  return (
    <div>
      <BeeCareOrderSideBar step={2} showSummary>
        <WBPPage title={t("323")} description={t("324")}>
          <Box mt={-1} mb={2}>
            <Grid container alignItems="center">
              <SummaryAddress shippingAddressID={beeCareOrder.address_id} />
              <Grid container direction="row" pt={1}>
                <Typography variant="h6">{t("Order summary")}</Typography>
              </Grid>
              {/* Group products by beehome */}
              {beeCareOrder.selectedHouses.map((house, index) => (
                <React.Fragment key={index}>
                  <Grid
                    container
                    direction="row"
                    pt={1}
                    alignItems="flex-start"
                  >
                    <Grid item xs={11}>
                      <Typography variant="h6">
                        {t("Beehome")} {house}
                      </Typography>
                    </Grid>
                    <Grid item xs={1} className={classes.deleteColumn}>
                      <IconButton
                        className={classes.deleteIconButton}
                        onClick={() => {
                          setBeeCareOrder((prev) => {
                            const newSelectedHouses =
                              prev.selectedHouses.filter((h) => h !== house);
                            const newAmount = newSelectedHouses.length;

                            // Remove excess vouchers if they exceed new beehome count
                            const newDiscountVouchers =
                              prev.discount_vouchers.slice(0, newAmount);

                            // Recalculate complete order totals with bulk pricing
                            const orderTotals = calculateCompleteOrderTotal({
                              selectedHouses: newSelectedHouses,
                              allShopifyVariants:
                                settings?.shopify?.products?.variants || [],
                              discount_vouchers: newDiscountVouchers,
                              donation: prev.donation || 0,
                              additionalOptions: prev.additionalOptions
                                ? prev.additionalOptions.filter(
                                    (option) => option.houseCode !== house
                                  )
                                : [],
                              additionalProducts: prev.additionalProducts || [],
                            });

                            return {
                              ...prev,
                              selectedHouses: newSelectedHouses,
                              amount: newAmount,
                              additionalOptions: prev.additionalOptions
                                ? prev.additionalOptions.filter(
                                    (option) => option.houseCode !== house
                                  )
                                : [],
                              discount_vouchers: newDiscountVouchers,
                              total_price: orderTotals.finalTotal,
                              variant: orderTotals.variant,
                              priceReduction: orderTotals.priceReduction,
                              price: orderTotals.originalPrice,
                            };
                          });
                        }}
                        size="small"
                        sx={{ padding: "1px", width: "20px", height: "20px" }}
                      >
                        <Delete sx={{ fontSize: "18px" }} />
                      </IconButton>
                    </Grid>
                    {/* BeeCare product */}
                    <Grid item xs={1} className={classes.countColumn}>
                      <Typography variant="body1">1</Typography>
                    </Grid>
                    <Grid item xs={7} className={classes.itemColumn}>
                      <Typography variant="body1">{t("BeeCare")}</Typography>
                      {beeCareOrder.priceReduction > 0 && (
                        <DiscountToolTip
                          priceReduction={
                            beeCareOrder.priceReduction /
                            beeCareOrder.selectedHouses.length
                          }
                          currency={currency}
                        />
                      )}
                    </Grid>
                    <Grid item xs={3} className={classes.priceColumn}>
                      <Typography variant="body1">
                        {currency + " " + parseFloat(price).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={1} className={classes.deleteColumn}></Grid>

                    {/* Additional options for this beehome */}
                    {beeCareOrder.additionalOptions &&
                      beeCareOrder.additionalOptions
                        .filter((option) => option.houseCode === house)
                        .map((option, optionIndex) => (
                          <React.Fragment key={optionIndex}>
                            {option.addOnDetails &&
                              option.addOnDetails.length > 0 &&
                              option.addOnDetails.map((addOn, addOnIndex) => (
                                <React.Fragment
                                  key={`${optionIndex}-${addOnIndex}`}
                                >
                                  <Grid
                                    item
                                    xs={1}
                                    className={classes.countColumn}
                                  >
                                    <Typography variant="body1">1</Typography>
                                  </Grid>
                                  <Grid
                                    item
                                    xs={7}
                                    className={classes.itemColumn}
                                  >
                                    <Typography variant="body1">
                                      {addOn.productTitle}
                                    </Typography>
                                  </Grid>
                                  <Grid
                                    item
                                    xs={3}
                                    className={classes.priceColumn}
                                  >
                                    <Typography variant="body1">
                                      {addOn.currency +
                                        " " +
                                        Number(addOn.price).toFixed(2)}
                                    </Typography>
                                  </Grid>
                                  <Grid
                                    item
                                    xs={1}
                                    className={classes.deleteColumn}
                                  >
                                    <IconButton
                                      className={classes.deleteIconButton}
                                      onClick={() =>
                                        history.push("/sendinbox/return-houses")
                                      }
                                      size="small"
                                      sx={{
                                        padding: "1px",
                                        width: "20px",
                                        height: "20px",
                                      }}
                                    >
                                      <Edit sx={{ fontSize: "18px" }} />
                                    </IconButton>
                                  </Grid>
                                </React.Fragment>
                              ))}
                          </React.Fragment>
                        ))}
                  </Grid>
                  <Divider style={{ margin: "10px 0" }} />
                </React.Fragment>
              ))}

              {((additionalProducts && additionalProducts.length > 0) ||
                beeCareOrder.donation > 0) && (
                <>
                  <Grid
                    container
                    direction="row"
                    pt={1}
                    alignItems="flex-start"
                  >
                    <Grid item xs={12}>
                      <Typography variant="h6">{t("520")}</Typography>
                    </Grid>
                  </Grid>
                  {additionalProducts &&
                    additionalProducts.length > 0 &&
                    additionalProducts.map((product, index) => (
                      <ProductLine
                        key={index}
                        product={product}
                        currency={currency}
                        onDelete={handleDelete}
                      />
                    ))}
                  {/* Donation section under Additional Products */}
                  {beeCareOrder.donation > 0 && (
                    <Grid container direction="row" py={1} alignItems="center">
                      <Grid item xs={1} className={classes.countColumn}>
                        <Typography variant="body1">1</Typography>
                      </Grid>
                      <Grid item xs={7} className={classes.itemColumn}>
                        <Typography variant="body1">{t("Donation")}</Typography>
                      </Grid>
                      <Grid item xs={3} className={classes.priceColumn}>
                        <Typography variant="body1">
                          {currency +
                            " " +
                            Number(beeCareOrder.donation).toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={1} className={classes.deleteColumn}>
                        <IconButton
                          className={classes.deleteIconButton}
                          onClick={submitDonation}
                          size="small"
                          sx={{ padding: "1px", width: "20px", height: "20px" }}
                        >
                          <Delete sx={{ fontSize: "16px" }}></Delete>
                        </IconButton>
                      </Grid>
                    </Grid>
                  )}
                </>
              )}

              {/* Vouchers section */}
              {beeCareOrder.discount_vouchers.length > 0 && (
                <>
                  <Grid
                    container
                    direction="row"
                    pt={1}
                    alignItems="flex-start"
                  >
                    <Grid item xs={12}>
                      <Typography variant="h6">{t("519")}</Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    item
                    direction="row"
                    py={1}
                    alignItems="flex-start"
                  >
                    <Grid item xs={1} className={classes.countColumn}>
                      <Typography variant="body1">
                        {beeCareOrder.discount_vouchers.length}
                      </Typography>
                    </Grid>
                    <Grid item xs={7} className={classes.itemColumn}>
                      <Typography variant="body1">{t("326")}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {t("329")}
                      </Typography>
                      {beeCareOrder.discount_vouchers.map((voucher, key) => (
                        <Typography
                          key={key}
                          variant="body2"
                          color="textSecondary"
                        >
                          {voucher.code}
                        </Typography>
                      ))}
                    </Grid>
                    <Grid item xs={3} className={classes.priceColumn}>
                      <Typography variant="body1">
                        {"- " +
                          currency +
                          " " +
                          parseFloat(
                            beeCareOrder.discount_vouchers.length * price
                          ).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={1} className={classes.deleteColumn}>
                      <IconButton
                        className={classes.deleteIconButton}
                        onClick={deleteAllDiscounts}
                        size="small"
                        sx={{ padding: "1px", width: "20px", height: "20px" }}
                      >
                        <Delete sx={{ fontSize: "16px" }}></Delete>
                      </IconButton>
                    </Grid>
                  </Grid>
                  <Divider style={{ margin: "10px 0" }} />
                </>
              )}

              <Divider className={classes.divider} />
              <Grid container direction="row" py={1}>
                <Grid item xs={1} className={classes.countColumn}>
                  &nbsp;
                </Grid>
                <Grid
                  item
                  xs={7}
                  className={classes.itemTextColumn}
                  alignSelf="center"
                >
                  <Typography variant="body1">{t("Total incl VAT")}</Typography>
                </Grid>
                <Grid
                  item
                  xs={4}
                  className={classes.priceColumn}
                  alignSelf="center"
                >
                  <Typography className={classes.totalPrice}>
                    {currency +
                      " " +
                      Number(beeCareOrder.total_price).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
              <Divider className={classes.divider} />
              <form
                onSubmit={submitVoucherCode}
                className={classes.formRoot}
                hidden={
                  beeCareOrder.selectedHouses.length <=
                  beeCareOrder.discount_vouchers.length
                }
              >
                <Grid
                  container
                  direction="row"
                  pt={1}
                  spacing={{ xs: 1 }}
                  alignItems="flex-start"
                  style={{ margin: 0 }}
                >
                  <Grid
                    item
                    xs={12}
                    sm={7}
                    className={classes.itemColumn}
                    alignSelf={"center"}
                  >
                    <Typography variant="body1">{t("328")}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={3} className={classes.priceColumn}>
                    <FormControl fullWidth>
                      <OutlinedInput
                        id="ticket_code"
                        variant="outlined"
                        name="ticket_code"
                        onChange={addVoucherHandler}
                        placeholder={t("329")}
                        value={voucherCode}
                        fullWidth
                        error={voucherError ? true : false}
                        classes={{ input: classes.padless }}
                      />
                      <FormHelperText
                        error={
                          voucherError && voucherError !== "" ? true : false
                        }
                      >
                        {voucherError && voucherError !== ""
                          ? voucherError
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2} className={classes.deleteColumn}>
                    <Button
                      id="button-sendinboxstep3-addvoucher"
                      variant="contained"
                      color="secondary"
                      type="submit"
                      classes={{ input: classes.padless }}
                      disableElevation
                      fullWidth
                    >
                      {t("106")}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>
          </Box>
          <form>
            <Box py={2}>
              <Grid container item xs={12} alignItems="center">
                <Grid item xs={12} sm={3} md={5}>
                  <Typography variant="body1">
                    {t("364")}
                    {isMobile && (
                      <>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          color="primary"
                          onClick={(event) => {
                            setAnchorEl(event.currentTarget);
                          }}
                          size="large"
                        >
                          <HelpOutline />
                        </IconButton>
                        <Popover
                          id={popoverId}
                          open={openPopover}
                          anchorEl={anchorEl}
                          onClose={() => {
                            setAnchorEl(null);
                          }}
                          onClick={() => {
                            setAnchorEl(null);
                          }}
                          anchorReference="anchorPosition"
                          anchorPosition={{ top: 25, left: 400 }}
                          anchorOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                        >
                          <DonationSummary showClose />
                        </Popover>
                      </>
                    )}
                  </Typography>
                </Grid>
                <Grid
                  container
                  item
                  direction={"column"}
                  xs={12}
                  sm={9}
                  md={7}
                  justifyContent="space-between"
                >
                  <Grid
                    container
                    item
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-end"
                    spacing={2}
                  >
                    <DonationSelector
                      currency={currency}
                      selectedDonation={beeCareOrder.donation}
                      submitDonation={submitDonation}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </form>
          <Box my={4}>
            <Grid container direction="column" py={1} alignItems="start">
              <Typography variant="h5" gutterBottom color={"primary"}>
                {t("517")}
              </Typography>
              <Typography variant="body1" sx={{ mr: 1 }}>
                {t("518")}
              </Typography>
            </Grid>

            <Grid container direction="row" py={1} alignItems="center">
              <AdditionalProducts
                onAddToCart={addProductToOrder}
              ></AdditionalProducts>
            </Grid>
          </Box>
          <Box my={4}>
            <Grid
              container
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <Grid item xs={12} sm={5}>
                <Button
                  id="button-sendinboxstep3-back"
                  className={`${classes.btnSize} ${classes.mobiWidth}`}
                  color="primary"
                  onClick={history.goBack}
                  variant="outlined"
                >
                  {t("404")}
                </Button>
              </Grid>
              <Grid item xs={12} sm={5}>
                <Button
                  id="button-sendinboxstep3-submitwithpayment"
                  className={`${classes.btnSize} ${classes.mobiWidth}`}
                  color="primary"
                  onClick={submitHandler}
                  variant="contained"
                  disableElevation
                >
                  {t("349")}
                  <ArrowForwardIosOutlined fontSize="small" />
                </Button>
              </Grid>
            </Grid>
          </Box>
        </WBPPage>
      </BeeCareOrderSideBar>
    </div>
  );
};

export default SendInBoxStep3;
